import { useState, useEffect, useRef } from "react";
import {
  Upload, Trash2, Plus, RefreshCw, Save, Edit, Edit3, X, FileText,
  AlertCircle, CheckCircle2, Home, Image, Camera, ExternalLink,
  Clock, Download, Eye, Receipt, Wallet, Euro, Loader2, Lock,
  Building2, MapPin, Users, Phone, Mail, Calendar, Shield,
  User, History, ArrowLeft, ArrowRight, Wrench, MessageSquare,
  ChevronDown, ChevronUp, Ban, Send, Copy, Smartphone, Key, QrCode,
  ShieldCheck, ShieldAlert, ShieldX, Thermometer, Scan
} from "lucide-react";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Textarea } from "../../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { API, getAuthHeaders, formatMontant, formatDate, openAuthedPdf } from "./helpers";
import { toast } from "sonner";
import { useConfigList } from "../../../hooks/useConfigList";
import { enteteTenant } from "../../../utils/enteteTenant";

export function DocumentsForm({ dossierId, tenantId, documents = [], verrouille, onUpdate, clientNom, dossierRef }) {
  const [uploading, setUploading] = useState(false);
  const [selectedType, setSelectedType] = useState("");
  const [deleting, setDeleting] = useState(null);
  const [validating, setValidating] = useState(null);
  const [validationResults, setValidationResults] = useState({});
  const [thumbnailUrls, setThumbnailUrls] = useState({});
  const fileInputRef = useRef(null);

  // Types de documents depuis la gouvernance (fallback hardcodé si vide)
  const { items: configDocTypes } = useConfigList("types_document");
  
  const FALLBACK_DOC_TYPES = [
    // Pièces identité
    { value: "piece_identite", label: "Pièce d'identité", validation_type: "CNI" },
    { value: "cni", label: "Pièce d'identité", validation_type: "CNI" },
    { value: "carte_vitale", label: "Carte vitale", validation_type: null },
    { value: "piece_mdph", label: "Pièce MDPH", validation_type: null },
    // Justificatifs financiers / logement
    { value: "justificatif_domicile", label: "Justificatif de domicile", validation_type: "JUSTIF_DOMICILE" },
    { value: "attestation_hebergement", label: "Attestation d'hébergement", validation_type: null },
    { value: "avis_imposition", label: "Avis d'imposition", validation_type: "AVIS_IMPOSITION" },
    { value: "avis_taxes_foncieres", label: "Avis de taxes foncières", validation_type: null },
    { value: "taxe_fonciere", label: "Avis de taxes foncières", validation_type: null },
    { value: "assurance_habitation", label: "Assurance habitation", validation_type: null },
    { value: "attestation_apa", label: "Attestation APA", validation_type: null },
    // RIB
    { value: "rib", label: "RIB", validation_type: null },
    { value: "rib_beneficiaire", label: "RIB bénéficiaire", validation_type: null },
    { value: "rib_mandataire", label: "RIB mandataire", validation_type: null },
    // Propriété logement
    { value: "acte_notarial", label: "Acte notarial", validation_type: null },
    { value: "accord_proprietaire", label: "Accord propriétaire", validation_type: null },
    { value: "titre_propriete", label: "Titre de propriété", validation_type: null },
    { value: "procuration", label: "Procuration", validation_type: null },
    // DAL Mobile (croquis + photos zone)
    { value: "dal", label: "DAL", validation_type: null },
    { value: "croquis_avant", label: "Croquis avant travaux", validation_type: null },
    { value: "croquis_apres", label: "Croquis après travaux", validation_type: null },
    { value: "photo_sab", label: "Photo salle de bain", validation_type: null },
    { value: "photo_fdc", label: "Photo façade", validation_type: null },
    { value: "photo_volet", label: "Photo volets", validation_type: null },
    { value: "photo_exterieur", label: "Photo extérieur", validation_type: null },
    { value: "photo_autre", label: "Photo (autre zone)", validation_type: null },
    // Mandat / contrat AMO
    { value: "mandat", label: "Mandat ANAH", validation_type: "MANDAT_MPR" },
    { value: "mandat_amo", label: "Mandat AMO", validation_type: null },
    { value: "devis_amo", label: "Devis AMO", validation_type: null },
    { value: "contrat_amo", label: "Contrat AMO", validation_type: null },
    { value: "facture_amo", label: "Facture AMO", validation_type: null },
    // Documents TEF / Process
    { value: "engagement", label: "Engagement", validation_type: null },
    { value: "rapport_visite", label: "Rapport de visite technique", validation_type: null },
    { value: "rapport_fin_projet", label: "Rapport de fin de projet", validation_type: null },
    { value: "fin_projet", label: "Document de fin de projet", validation_type: null },
    { value: "fiche_renseignement", label: "Fiche de renseignement", validation_type: null },
    { value: "plan_financement", label: "Plan de financement", validation_type: null },
    { value: "plan_financement_signe", label: "Plan de financement signé", validation_type: null },
    { value: "plan_financement_docusign", label: "Plan de financement (DocuSign)", validation_type: null },
    { value: "docusign_summary", label: "Résumé signature électronique", validation_type: null },
    { value: "demande_avance", label: "Demande d'avance", validation_type: null },
    { value: "devis_avance", label: "Devis d'avance", validation_type: null },
    { value: "doc_paiement", label: "Document de paiement", validation_type: null },
    { value: "octroi_anah", label: "Lettre d'octroi ANAH", validation_type: null },
    // Travaux / installateurs
    { value: "devis", label: "Devis installateur", validation_type: "DEVIS" },
    { value: "devis_installateur", label: "Devis installateur", validation_type: "DEVIS" },
    { value: "facture", label: "Facture", validation_type: "FACTURE" },
    { value: "facture_travaux", label: "Facture travaux", validation_type: "FACTURE" },
    { value: "attestation_honneur", label: "Attestation sur l'honneur", validation_type: "AH_CEE" },
    { value: "certificat_cee", label: "Certificat CEE", validation_type: null },
    { value: "formulaire_cee", label: "Formulaire CEE", validation_type: null },
    // Génériques
    { value: "photo", label: "Photo", validation_type: null },
    { value: "autre", label: "Autre document", validation_type: null }
  ];

  // Priorité: config-listes > endpoint custom > fallback
  const [documentTypes, setDocumentTypes] = useState([]);

  // Charger les types de documents: priorité config-listes > endpoint custom > fallback
  useEffect(() => {
    // Si on a des types depuis useConfigList, les transformer
    if (configDocTypes.length > 0) {
      const mapped = configDocTypes.map(item => ({
        value: item.id,
        label: item.label,
        validation_type: item.validation_type || null,
      }));
      setDocumentTypes(mapped);
      return;
    }
    
    // Sinon, essayer l'endpoint custom
    const loadDocumentTypes = async () => {
      try {
        const token = localStorage.getItem('session_token');
        const res = await fetch(`${API}/api/metier/config/document-types`, {
          headers: {
            ...enteteTenant(tenantId),
            ...(token && { "Authorization": `Bearer ${token}` })
          }
        });
        
        if (res.ok) {
          const data = await res.json();
          if (data.types && data.types.length > 0) {
            setDocumentTypes(data.types);
          } else {
            setDocumentTypes(FALLBACK_DOC_TYPES);
          }
        } else {
          setDocumentTypes(FALLBACK_DOC_TYPES);
        }
      } catch {
        setDocumentTypes(FALLBACK_DOC_TYPES);
      }
    };
    loadDocumentTypes();
  }, [configDocTypes, tenantId]);

  // Utiliser les types chargés ou les défauts
  const DOCUMENT_TYPES = documentTypes.length > 0 ? documentTypes : FALLBACK_DOC_TYPES;

  // Charger les miniatures des documents (images ET PDFs)
  useEffect(() => {
    const loadThumbnails = async () => {
      const token = localStorage.getItem('session_token');
      if (!token) return;
      
      for (const doc of documents) {
        // Ne pas recharger si déjà en cache
        if (thumbnailUrls[doc.id]) continue;
        
        const isImage = doc.fichier_type?.startsWith('image/') || 
          doc.nom?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
        const isPdf = doc.fichier_type === 'application/pdf' || 
          doc.nom?.match(/\.pdf$/i);
        
        // Charger les miniatures pour images ET PDFs
        if (isImage || isPdf) {
          try {
            // Utiliser l'endpoint thumbnail pour tous les types supportés
            const res = await fetch(`${API}/api/metier/documents/${doc.id}/thumbnail?width=120`, {
              headers: {
                ...enteteTenant(tenantId),
                "Authorization": `Bearer ${token}`
              },
              credentials: 'include'
            });
            
            if (res.ok) {
              const blob = await res.blob();
              const url = URL.createObjectURL(blob);
              setThumbnailUrls(prev => ({ ...prev, [doc.id]: url }));
            } else if (isImage) {
              // Fallback pour les images: utiliser download direct
              const downloadRes = await fetch(`${API}/api/metier/documents/${doc.id}/download`, {
                headers: {
                  ...enteteTenant(tenantId),
                  "Authorization": `Bearer ${token}`
                },
                credentials: 'include'
              });
              
              if (downloadRes.ok) {
                const blob = await downloadRes.blob();
                const url = URL.createObjectURL(blob);
                setThumbnailUrls(prev => ({ ...prev, [doc.id]: url }));
              }
            }
          } catch (err) {
            console.error("Erreur chargement miniature:", err);
          }
        }
      }
    };
    
    if (documents.length > 0) {
      loadThumbnails();
    }
  }, [documents, tenantId]);

  // Générer un nom de document cohérent
  const generateDocumentName = (docType, originalName) => {
    const ext = originalName.split('.').pop()?.toLowerCase() || 'pdf';
    const typeLabel = DOCUMENT_TYPES.find(t => t.value === docType)?.label || docType;
    const clientPart = clientNom ? clientNom.replace(/[^a-zA-Z0-9]/g, '_').toUpperCase() : 'CLIENT';
    const refPart = dossierRef || dossierId.slice(-6);
    const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    
    return `${refPart}_${clientPart}_${typeLabel.replace(/[^a-zA-Z0-9]/g, '_')}_${date}.${ext}`;
  };

  // Valider un document via le moteur de validation
  const validateDocument = async (docId, file, docType) => {
    const validationType = DOCUMENT_TYPES.find(t => t.value === docType)?.validation_type;
    if (!validationType) return null; // Pas de validation pour ce type
    
    setValidating(docId);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("type_document_attendu", validationType);
      formData.append("dossier_id", dossierId);
      
      const res = await fetch(`${API}/api/documents/validate`, {
        method: "POST",
        body: formData
      });
      
      if (res.ok) {
        const result = await res.json();
        setValidationResults(prev => ({ ...prev, [docId]: result }));
        return result;
      }
    } catch (err) {
      console.error("Erreur validation:", err);
    } finally {
      setValidating(null);
    }
    return null;
  };

  // Re-valider un document existant
  const handleRevalidate = async (doc) => {
    setValidating(doc.id);
    try {
      const token = localStorage.getItem('session_token');
      // Télécharger le fichier d'abord
      const res = await fetch(`${API}/api/metier/documents/${doc.id}/download`, {
        headers: { 
          ...enteteTenant(tenantId),
          ...(token && { "Authorization": `Bearer ${token}` })
        },
        credentials: 'include'
      });
      if (res.ok) {
        const blob = await res.blob();
        const file = new File([blob], doc.nom || 'document', { type: blob.type });
        await validateDocument(doc.id, file, doc.type_document);
      }
    } catch (err) {
      console.error("Erreur re-validation:", err);
    } finally {
      setValidating(null);
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0 || !selectedType) return;

    setUploading(true);
    let uploadedCount = 0;
    let failedCount = 0;
    try {
      // BUG-02 fix 2026-05-03 — itere sur TOUS les fichiers selectionnes
      // (avant : seul e.target.files[0] etait pris). Permet upload
      // multiple : recto/verso CNI, pages multiples avis impôts, etc.
      // Pour des types differents (CNI + Taxe fonciere), l'utilisateur
      // doit faire 2 selections successives — le type est associe a
      // toute la batch courante.
      for (const file of files) {
        // 1. Valider le document AVANT l'upload (par fichier)
        const validationType = DOCUMENT_TYPES.find(t => t.value === selectedType)?.validation_type;
        let validationResult = null;

        if (validationType && (file.type.startsWith('image/') || file.type === 'application/pdf')) {
          const formData = new FormData();
          formData.append("file", file);
          formData.append("type_document_attendu", validationType);
          formData.append("dossier_id", dossierId);

          try {
            const valRes = await fetch(`${API}/api/documents/validate`, {
              method: "POST",
              body: formData
            });
            if (valRes.ok) {
              validationResult = await valRes.json();
            }
          } catch (valErr) {
            console.warn("Validation non disponible:", valErr);
          }
        }

        if (validationResult?.status === "INVALID") {
          const reasons = validationResult.reasons?.join(", ") || "Document invalide";
          toast.warning(`${file.name}: invalide (${reasons}, score: ${validationResult.score}/100)`, { duration: 5000 });
        }

        // 2. Upload du document
        const formData = new FormData();
        formData.append("file", file);
        formData.append("type_document", selectedType);
        const nomDocument = generateDocumentName(selectedType, file.name);
        formData.append("nom", nomDocument);

        if (validationResult) {
          formData.append("validation_status", validationResult.status);
          formData.append("validation_score", validationResult.score);
        }

        try {
          const res = await fetch(`${API}/api/metier/dossiers/${dossierId}/documents`, {
            method: "POST",
            headers: {
              ...enteteTenant(tenantId),
              ...(() => {
                const token = localStorage.getItem('session_token');
                return token ? { "Authorization": `Bearer ${token}` } : {};
              })()
            },
            credentials: 'include',
            body: formData
          });

          if (res.ok) {
            const newDoc = await res.json();
            if (validationResult && newDoc.id) {
              setValidationResults(prev => ({ ...prev, [newDoc.id]: validationResult }));
            }
            uploadedCount++;
          } else {
            failedCount++;
            const err = await res.json().catch(() => ({}));
            toast.error(`Upload échoué (${file.name}) : ${err.detail || res.status}`);
          }
        } catch (uploadErr) {
          failedCount++;
          console.error(`Erreur upload ${file.name}:`, uploadErr);
        }
      }

      // Resume final
      if (uploadedCount > 0) {
        toast.success(`${uploadedCount} fichier(s) uploadé(s)${failedCount > 0 ? ` (${failedCount} échec(s))` : ""}`);
        onUpdate?.();
        setSelectedType("");
      }
    } catch (err) {
      console.error("Erreur upload:", err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Télécharger un document
  const handleDownload = async (doc) => {
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API}/api/metier/documents/${doc.id}/download`, {
        headers: { 
          ...enteteTenant(tenantId),
          ...(token && { "Authorization": `Bearer ${token}` })
        },
        credentials: 'include'
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.nom || doc.fichier_nom || 'document';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (err) {
      console.error("Erreur téléchargement:", err);
    }
  };

  // Visualiser un document (ouvre dans un nouvel onglet).
  // Fix pop-up blocker : openAuthedPdf ouvre l'onglet DANS le geste utilisateur
  // (avant l'await), sinon window.open post-fetch est bloqué par le navigateur.
  const handleView = async (doc) => {
    const token = localStorage.getItem('session_token');
    await openAuthedPdf({
      url: `${API}/api/metier/documents/${doc.id}/download`,
      headers: {
        ...enteteTenant(tenantId),
        ...(token && { "Authorization": `Bearer ${token}` }),
      },
      onError: (msg) => toast.error(msg || "Erreur à l'ouverture"),
    });
  };

  // Supprimer un document
  const handleDelete = async (doc) => {
    if (!window.confirm(`Supprimer le document "${doc.nom}" ?`)) return;
    
    setDeleting(doc.id);
    try {
      const token = localStorage.getItem('session_token');
      const res = await fetch(`${API}/api/metier/documents/${doc.id}`, {
        method: "DELETE",
        headers: { 
          ...enteteTenant(tenantId),
          ...(token && { "Authorization": `Bearer ${token}` })
        },
        credentials: 'include'
      });
      if (res.ok) {
        onUpdate?.();
      }
    } catch (err) {
      console.error("Erreur suppression:", err);
    } finally {
      setDeleting(null);
    }
  };

  // Formater la date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return formatDate(dateStr);
    } catch {
      return '';
    }
  };

  return (
    <div className="bg-[var(--oh-bg-elevated)] border border-[var(--oh-border)] rounded-xl p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-[var(--oh-text)] font-medium">Documents GED</h3>
      </div>

      {/* Formulaire d'upload */}
      {!verrouille && (
        <div className="bg-[var(--oh-bg-elevated)] rounded-lg p-4 mb-6">
          <p className="text-[var(--oh-text)] text-sm mb-3">Ajouter un document</p>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
            <div className="flex-1">
              <p className="text-[var(--oh-text-muted)] text-xs mb-1">Type de document</p>
              <Select value={selectedType} onValueChange={setSelectedType}>
                <SelectTrigger className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)]">
                  <SelectValue placeholder="Sélectionner le type" />
                </SelectTrigger>
                <SelectContent>
                  {DOCUMENT_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={handleFileSelect}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={!selectedType || uploading}
                className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)] w-full sm:w-auto"
              >
                {uploading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Choisir un fichier
                  </>
                )}
              </Button>
              <Button
                onClick={() => {
                  if (selectedType) {
                    window.open(`/scanner?type=document&docType=${selectedType}&dossierId=${dossierId}`, '_blank');
                  }
                }}
                disabled={!selectedType || uploading}
                variant="outline"
                className="border-[var(--oh-accent)] text-[var(--oh-accent-text)] hover:bg-[var(--oh-accent-bg)] w-full sm:w-auto"
              >
                <Scan className="w-4 h-4 mr-2" />
                Scanner
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Liste des documents - Format Vignette/Grille */}
      {documents.length === 0 ? (
        <div className="text-center py-8">
          <FileText className="w-12 h-12 mx-auto text-[var(--oh-text-secondary)] mb-3" />
          <p className="text-[var(--oh-text-muted)]">Aucun document</p>
          <p className="text-[var(--oh-text-secondary)] text-sm mt-1">Ajoutez vos documents ci-dessus</p>
        </div>
      ) : (
        <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 xl:grid-cols-12 gap-2" data-testid="documents-grid">
          {documents.map((doc) => {
            const validation = validationResults[doc.id] || doc.validation;
            const docTypeInfo = DOCUMENT_TYPES.find(t => t.value === doc.type_document);
            const canValidate = docTypeInfo?.validation_type && 
              (doc.fichier_type?.startsWith('image/') || doc.fichier_type === 'application/pdf' || !doc.fichier_type);
            const isImage = doc.fichier_type?.startsWith('image/') || 
              doc.nom?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
            const isPdf = doc.fichier_type === 'application/pdf' || 
              doc.nom?.match(/\.pdf$/i);
            const hasThumbnail = thumbnailUrls[doc.id];
            
            return (
            <div
              key={doc.id}
              className="group rounded-lg border hover:border-[var(--oh-border)] transition-all overflow-hidden cursor-pointer"
              style={{ background: "var(--oh-bg-surface)", borderColor: "var(--oh-border)" }}
              onClick={() => handleView(doc)}
              data-testid={`doc-card-${doc.type_document}`}
            >
              {/* Vignette compacte */}
              <div className="relative w-full aspect-[4/3] bg-[var(--oh-bg-elevated)] overflow-hidden">
                {hasThumbnail ? (
                  <>
                    <img
                      src={thumbnailUrls[doc.id]}
                      alt={doc.nom}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                    />
                    {isPdf && (
                      <div className="absolute top-1 left-1 bg-red-600 text-[var(--oh-accent-contrast)] text-[8px] px-1 py-0.5 rounded font-medium">PDF</div>
                    )}
                  </>
                ) : (isImage || isPdf) ? (
                  <div className="w-full h-full flex items-center justify-center">
                    <RefreshCw className="w-4 h-4 text-[var(--oh-text-secondary)] animate-spin" />
                  </div>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center">
                    <FileText className="w-7 h-7 text-[var(--oh-accent-text)]" />
                    <span className="text-[8px] text-[var(--oh-text-muted)] mt-0.5 uppercase">
                      {doc.fichier_type?.split('/')[1]?.substring(0, 4) || 'DOC'}
                    </span>
                  </div>
                )}
                {/* Badge validation */}
                {validation && (
                  <div className="absolute top-1 right-1">
                    {validation.status === "VALID" && (
                      <div className="bg-green-500 rounded-full p-0.5"><CheckCircle2 className="w-3 h-3 text-[var(--oh-accent-contrast)]" /></div>
                    )}
                    {validation.status === "NEEDS_REVIEW" && (
                      <div className="bg-yellow-500 rounded-full p-0.5"><AlertCircle className="w-3 h-3 text-[var(--oh-accent-contrast)]" /></div>
                    )}
                    {validation.status === "INVALID" && (
                      <div className="bg-red-500 rounded-full p-0.5"><AlertCircle className="w-3 h-3 text-[var(--oh-accent-contrast)]" /></div>
                    )}
                  </div>
                )}
                {/* Actions overlay on hover */}
                <div className="absolute inset-0 bg-[var(--oh-bg)] group-hover:bg-[var(--oh-bg)] transition-colors flex items-center justify-center gap-0.5 opacity-0 group-hover:opacity-100">
                  <Button size="sm" variant="ghost" className="bg-[var(--oh-bg-surface)] backdrop-blur-sm text-[var(--oh-text)] h-6 w-6 p-0 rounded-full" onClick={(e) => { e.stopPropagation(); handleView(doc); }}>
                    <ExternalLink className="w-3 h-3" />
                  </Button>
                  <Button size="sm" variant="ghost" className="bg-[var(--oh-bg-surface)] backdrop-blur-sm text-[var(--oh-text)] h-6 w-6 p-0 rounded-full" onClick={(e) => { e.stopPropagation(); handleDownload(doc); }}>
                    <Download className="w-3 h-3" />
                  </Button>
                  {!verrouille && !doc.signe && (
                    <Button size="sm" variant="ghost" className="bg-[var(--oh-error-bg)] backdrop-blur-sm text-[var(--oh-text)] h-6 w-6 p-0 rounded-full" onClick={(e) => { e.stopPropagation(); handleDelete(doc); }} disabled={deleting === doc.id}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </div>
              {/* Info sous la vignette — label métier en principal, fichier brut en sous-titre.
                  Évite l'affichage des noms Zoho techniques type "cde04e_ROGER_..._jpeg". */}
              <div className="p-1">
                <p className="text-[9px] font-medium truncate" style={{ color: "var(--oh-text)" }} title={docTypeInfo?.label || doc.nom}>
                  {docTypeInfo?.label || doc.nom}
                </p>
                {docTypeInfo?.label && doc.nom && (
                  <p className="text-[8px] truncate" style={{ color: "var(--oh-text-muted)" }} title={doc.nom}>
                    {doc.nom}
                  </p>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
